import React, { useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { routeConstants } from '../../../constants/routeConstants';
import { 
  GraduationCap, BookOpen, Bus, Crown, ShieldCheck, 
  Library, Users, Lock, Search, Bell, LogOut, ChevronRight 
} from 'lucide-react';

// Static mapping for role display details based on slug
const ROLE_DETAILS_MAP = {
  'teacher': { icon: GraduationCap, desc: 'Manage classes, attendance, marks and assignments.', activeStr: '38 active' },
  'student': { icon: BookOpen, desc: 'View timetables, results, fees and study material.', activeStr: '1,386 active' },
  'transport': { icon: Bus, desc: 'Track routes, vehicles and driver schedules.', activeStr: '12 routes' },
  'principal': { icon: Crown, desc: 'Oversee academics, staff and institution reports.', activeStr: '1 account' },
  'admin': { icon: ShieldCheck, desc: 'Full control over configuration, users and policies.', activeStr: 'Recommended' },
  'super-admin': { icon: ShieldCheck, desc: 'Full control over configuration, users and policies.', activeStr: 'Recommended' },
  'library': { icon: Library, desc: 'Manage books, manage catalog and track returns.', activeStr: '8,229 titles' },
  'hr': { icon: Users, desc: 'Handle payroll, recruitment and staff resources.', activeStr: '90 staff' },
};

const RoleSelectionPage = () => {
  const { user, setActiveRole, logout } = useAuth();
  const navigate = useNavigate();
  const [selectedRole, setSelectedRole] = useState(null);

  // If no user, protect route handles it, but just in case:
  if (!user) {
    return <Navigate to={routeConstants.LOGIN} replace />;
  }

  // If user has already selected a role, they shouldn't be here typically, but we allow them to change roles.
  
  const handleContinue = () => {
    if (selectedRole) {
      setActiveRole(selectedRole);
      navigate(routeConstants.DASHBOARD);
    }
  };

  const handleLogout = () => {
    logout();
    navigate(routeConstants.LOGIN);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col font-sans">
      
      {/* Top Navigation */}
      <header className="bg-white border-b border-gray-200 h-16 flex items-center justify-between px-6">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center text-white">
            <GraduationCap className="w-5 h-5" />
          </div>
          <div>
            <h1 className="font-bold text-gray-900 leading-tight">SchoolERP</h1>
            <p className="text-xs text-gray-500 leading-tight">Admin Panel</p>
          </div>
        </div>

        <div className="hidden md:flex items-center text-sm text-gray-500 gap-2">
          <span>Access Control</span>
          <ChevronRight className="w-4 h-4" />
          <span className="font-medium text-gray-900">Role Selection</span>
        </div>

        <div className="flex items-center gap-4">
          <div className="relative hidden md:block">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input 
              type="text" 
              placeholder="Search roles..." 
              className="pl-9 pr-4 py-2 bg-gray-100 border-transparent rounded-lg text-sm focus:bg-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none w-64"
            />
          </div>
          <button className="text-gray-400 hover:text-gray-600">
            <Bell className="w-5 h-5" />
          </button>
          <div className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center font-medium text-sm">
            {user.first_name ? user.first_name.charAt(0) : 'U'}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 max-w-5xl mx-auto w-full px-4 py-12 flex flex-col items-center">
        
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center gap-2 text-sm text-gray-500 mb-4">
            <Lock className="w-4 h-4" />
            Secure Login Portal
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-3">Select Your Role</h2>
          <p className="text-gray-500 max-w-lg mx-auto">
            Choose how you'd like to access the SchoolERP workspace. Your dashboard and permissions adapt to the role you pick.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 w-full mb-8">
          {user.roles && user.roles.map((role) => {
            const isSelected = selectedRole?.id === role.id;
            const details = ROLE_DETAILS_MAP[role.slug] || { icon: ShieldCheck, desc: 'Access the system with this role.', activeStr: 'Active' };
            const Icon = details.icon;
            const isRecommended = role.slug.includes('admin');

            return (
              <div 
                key={role.id}
                onClick={() => setSelectedRole(role)}
                className={`relative p-5 rounded-xl border-2 cursor-pointer transition-all ${
                  isSelected 
                    ? 'border-blue-500 bg-blue-50/50 shadow-sm' 
                    : 'border-gray-200 bg-white hover:border-blue-300 hover:shadow-sm'
                }`}
              >
                {isRecommended && (
                  <span className="absolute top-4 right-4 bg-blue-600 text-white text-xs font-semibold px-2 py-0.5 rounded-full">
                    Recommended
                  </span>
                )}
                
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-4 ${isSelected ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600'}`}>
                  <Icon className="w-5 h-5" />
                </div>
                
                <div className="flex justify-between items-center mb-2">
                  <h3 className={`font-semibold text-lg ${isSelected ? 'text-blue-700' : 'text-gray-900'}`}>{role.name}</h3>
                  <ChevronRight className={`w-4 h-4 ${isSelected ? 'text-blue-500' : 'text-gray-300'}`} />
                </div>
                
                <p className="text-sm text-gray-500 mb-4 line-clamp-2 min-h-[40px]">
                  {details.desc}
                </p>
                
                <p className="text-xs text-gray-400 font-medium">
                  {details.activeStr}
                </p>
              </div>
            );
          })}

          {/* Help Card */}
          <div className="relative p-5 rounded-xl border border-transparent bg-gradient-to-br from-blue-600 to-blue-800 text-white overflow-hidden shadow-md">
            <div className="absolute inset-0 opacity-20 bg-[url('https://images.unsplash.com/photo-1541339907198-e08756dedf3f?auto=format&fit=crop&q=80&w=600')] bg-cover bg-center"></div>
            <div className="relative z-10 h-full flex flex-col justify-end">
              <h3 className="font-semibold text-lg mb-2">Can't find your role?</h3>
              <p className="text-sm text-blue-100 mb-4">
                Contact your administrator to get the right access assigned to your account.
              </p>
            </div>
          </div>
        </div>

        <button 
          onClick={handleContinue}
          disabled={!selectedRole}
          className={`px-8 py-3 rounded-lg font-medium text-white shadow-sm transition-colors ${
            selectedRole ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gray-300 cursor-not-allowed'
          }`}
        >
          {selectedRole ? `Continue as ${selectedRole.name}` : 'Select a role to continue'}
        </button>
        
        <p className="text-xs text-gray-400 mt-6 max-w-md text-center">
          Note: Determine which modules and data you can view or edit. Switching roles requires re-authentication.
        </p>

      </main>

      {/* Bottom Footer */}
      <footer className="bg-white border-t border-gray-200 p-4 flex items-center justify-between mt-auto">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold">
            {user.first_name ? user.first_name.charAt(0) : 'U'}
          </div>
          <div>
            <p className="font-medium text-gray-900 text-sm leading-tight">{user.full_name || 'Admin User'}</p>
            <p className="text-xs text-gray-500 leading-tight">{user.email}</p>
          </div>
        </div>
        <button onClick={handleLogout} className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors">
          <LogOut className="w-5 h-5" />
        </button>
      </footer>
    </div>
  );
};

export default RoleSelectionPage;
